import { span } from '@ns/resilience';
import { resolveAddress } from './address';
import { toWireBody } from './body';
import type { Exchange, Flight } from './coalesce';
import { cachedExchangeOf, flightOf, joinFlight } from './coalesce';
import { logRetry, startCallLog } from './debug-events';
import { deliverFailure } from './delivery';
import type { Deadline, EndedBy } from './deadline';
import { mergeSignals, startDeadline } from './deadline';
import type { HttpError, HttpErrorOptions } from './errors';
import {
  AttemptTimeoutError,
  isHttpError,
  NetworkError,
  RequestAbortError,
  RequestTimeoutError,
  RequestValidationError,
  UnsupportedBodyError,
  UnsupportedRequestError,
} from './errors';
import type { Outcome, RetryOption } from './handlers';
import {
  beforeStateOf,
  refuseHandlerRewrites,
  registerGlobalAfter,
  registerGlobalBefore,
  runAfter,
  runBefore,
} from './handlers';
import { mergeHeaders, toHeaders, withRequestId } from './headers';
import { newCallId, randomHex } from './ids';
import { validateOptions } from './limits';
import { forThisCaller } from './per-caller';
import { finishRequestAttempt } from './request-attempt';
import type { AnswerOrigin } from './request-attempt';
import { observedOf } from './response-body';
import {
  decodedResultOf,
  discardStream,
  rawResultOf,
  streamOf,
} from './results';
import type { FailedAttempt } from './retry';
import {
  mayRetry,
  notifyAttemptStarted,
  notifyRetryScheduled,
  retryConfigOf,
  retryDelayMsOf,
  retryPolicyOf,
  sleep,
  worthWaiting,
} from './retry';
import {
  addTrace,
  beforeTraceOf,
  noTrace,
  traceFor,
  traceOf,
  headersForTrace,
} from './trace';
import { noTransport, transportFor } from './transport';
import type {
  Trace,
  DecodedRequestResult,
  DecodedFailureResult,
  DecodedRequestOptions,
  DecodedRequestOptionsWithContext,
  ExplicitChoiceGate,
  RequiredBodyRequestOptions,
  OptionalBodyRequestOptions,
  RequiredBodyRequestOptionsWithContext,
  OptionalBodyRequestOptionsWithContext,
  ThrowOnErrorOptions,
  GlobalAfterRequestHandler,
  GlobalBeforeRequestHandler,
  GlobalRequestHeaders,
  Policy,
  PolicyContext,
  Request,
  RawHeadRequestOptions,
  RawHeadRequestOptionsWithContext,
  RawHeadRequestResult,
  RawNonHeadRequestOptions,
  RawNonHeadRequestOptionsWithContext,
  RawRequestResult,
  RequestOptions,
  RequestResult,
  UntracedResult,
  RetryConfig,
  Unregister,
} from './types';
import { approved } from './validate';

/**
 * Runs one boundary step and relabels whatever it throws.
 *
 * The steps themselves know nothing about our errors: they let the platform
 * throw its `TypeError` and this decides which failure that is. That keeps the
 * classification in one readable place instead of spread over three modules.
 */
function rethrowAs<T>(step: () => T, relabel: (cause: unknown) => Error): T {
  try {
    return step();
  } catch (error) {
    throw relabel(error);
  }
}

/**
 * The same relabelling for a step that can also REJECT.
 *
 * `rethrowAs` cannot serve here: it returns the promise before the rejection has
 * happened, so the `catch` around it never fires. The `await` inside the `try` is
 * what makes the rejection reachable, and it is why this is a second function
 * rather than a wider signature on the first.
 */
async function rethrowAsAsync<T>(
  step: () => Promise<T>,
  relabel: (cause: unknown) => Error,
): Promise<T> {
  try {
    return await step();
  } catch (error) {
    throw relabel(error);
  }
}

/* ---------------------------------------------------------------------------
 * The policies. One list, wrapped outside in.
 * ------------------------------------------------------------------------- */

/**
 * The address of the call, or as much of it as parses.
 *
 * An address that does not resolve is NOT reported here, which is what the
 * fallback is for. `run` reaches the same `resolveAddress` a moment later and
 * relabels it as an `UnsupportedRequestError` from inside an attempt, where the
 * `after` handlers still hear about it. Failing here instead would move that
 * failure outside the observation window, and would make which error a caller
 * sees depend on whether they happen to have set a policy.
 */
function addressOf(options: RequestOptions): { origin: string; url: string } {
  try {
    const url = resolveAddress(options);
    // Cannot throw: what comes back is what a `URL` has already parsed once.
    return { origin: new URL(url).origin, url };
  } catch {
    // The caller's own `url` back, and an EMPTY origin rather than a guessed
    // one: an address that did not resolve has no host to key per-host state on,
    // and a policy reading `''` can tell that apart from a real origin.
    return { origin: '', url: options.url };
  }
}

/**
 * What every policy in the list is handed: the ADDRESS of the call, frozen.
 *
 * ONE OBJECT PER CALL, shared by the whole list (PO4). A shared identity is what
 * lets two policies agree on what they are looking at, and the freeze is what
 * stops the first from rewriting it for the second.
 *
 * Built from the caller's OWN options, before the `before` handlers run (PO22).
 * The handlers run inside the policies, so there is nothing of theirs to read
 * yet — and a handler that switches host would otherwise move the origin a
 * breaker is watching under it, halfway through a call it has already admitted.
 *
 * `context` is spread in rather than assigned, because `'context' in context`
 * is the check a policy writes and a key holding `undefined` answers it wrongly
 * (PO5).
 */
function policyContext(
  options: RequestOptions,
  signal: AbortSignal,
  callId: string,
): PolicyContext {
  const { origin, url } = addressOf(options);
  return Object.freeze({
    ...('context' in options ? { context: options.context } : {}),
    callId,
    method: options.method ?? 'GET',
    origin,
    signal,
    url,
  });
}

/**
 * The call with the list wrapped around it, the FIRST policy outermost (PO3).
 *
 * Reading order is nesting order, which is the only arrangement a caller can
 * predict without counting how many there are. `reduceRight` builds that from
 * the inside out: the last one ends up nearest the call.
 *
 * A `Policy` in the RETRY position is appended, so it lands innermost -- inside
 * every `policies` entry and outside the attempts, which is exactly the place
 * the built-in loop occupies. That position is the whole reason the option
 * exists rather than being a slot in `policies`: from `policies` a wrapper sits
 * one layer FURTHER OUT, and a breaker beside it would then count once per
 * ATTEMPT instead of once per call and trip inside a single call.
 *
 * ONE list, ONE `reduceRight`, ONE context. Wrapping the retry policy separately
 * at the call site would mean a second `policyContext`, and PO4 is that all
 * policies of a call share ONE frozen object -- two would let a breaker in
 * `policies` and the caller's loop disagree about what they are looking at.
 * Sharing it is also what RY5 rests on: `context.method` is how a caller who
 * brought their own loop decides whether a POST may be repeated, since nothing
 * in this library decides that for them and the config a built-in loop would
 * read is not in play here at all.
 *
 * Without either this hands the call straight back, so an empty `policies`
 * behaves exactly like not setting it (PO1) through the same code path rather
 * than through the same promise made twice. It is also why the context is built
 * here rather than by the caller: nothing to wrap, nobody to hand one to. There
 * is no queue to short-circuit either, so the conversion below has nothing to
 * do on that path: a call with no policies around it converts for itself.
 *
 * `failureIfEnded` is the failure this call has already ended on, or `undefined`
 * while it is still running.
 */
function guarded<T>(
  options: RequestOptions,
  deadline: Deadline,
  call: () => Promise<T>,
  callId: string,
  failureFor: (by: EndedBy) => HttpError,
): () => Promise<T> {
  const ownLoop = retryPolicyOf(options.retry);
  const wrappers: readonly Policy[] = [
    ...(options.policies ?? []),
    ...(ownLoop === undefined ? [] : [ownLoop]),
  ];
  if (wrappers.length === 0) return call;

  const context = policyContext(options, deadline.signal, callId);

  /**
   * The policy boundary, where a call that never went out is given the class a
   * caller was promised.
   *
   * A call still WAITING in a queue is not in flight, so `bulkhead` and
   * `throttle` let it go by rejecting with the signal's own reason -- a platform
   * `DOMException`, deliberately, because neither of them can tell a caller
   * leaving from an elapsed deadline. This is the layer that can, and answering
   * here rather than at the far end of the pipeline is what gets that answer to
   * the policies still OUTSIDE the queue: a breaker reading a raw rejection has
   * no failure kind to read and counts a caller closing their tab as an upstream
   * that failed everybody.
   *
   * IDENTITY, never the name, for the reason `startDeadline` gives: a caller's
   * own `AbortSignal.timeout` rejects with a `DOMException` named `TimeoutError`,
   * and a name would report their cancellation as our deadline. Everything else
   * a policy rejects with is its own and travels untouched.
   */
  const classified = async (run: () => Promise<T>): Promise<T> => {
    try {
      return await run();
    } catch (error) {
      const { signal } = deadline;
      if (!signal.aborted || error !== signal.reason) throw error;
      // `endedBy` answers `undefined` only while the call is still running, and
      // the line above has already established that this signal is down. The
      // branch is unreachable and kept because the answer is optional, not
      // because a call can arrive here without one.
      const endedBy = deadline.endedBy();
      throw endedBy === undefined ? error : failureFor(endedBy);
    }
  };

  return wrappers.reduceRight<() => Promise<T>>(
    (inner, wrapper) => () => classified(() => wrapper(inner, context)),
    call,
  );
}

/**
 * Whether this failure is one this library labelled, asked where the answer is
 * only used to WRITE SOMETHING DOWN about it.
 *
 * @remarks
 * Total, and that is the whole of why it exists. `isHttpError` is a duck-typed
 * read and says so in its own `@throws`: a value whose `failureKind` getter
 * throws propagates that out instead of answering. Every caller of this helper
 * is on its way to rethrowing the failure, so an unguarded read would hand the
 * caller their getter's error in place of what they threw -- bookkeeping taking
 * the failure away from the person it belongs to.
 *
 * A `before` handler's throw is exactly such a value's route in, because it
 * travels unwrapped (BE7) while a transport failure is wrapped in a
 * `NetworkError` first and never reaches the read as itself.
 *
 * Unlabelled is the safe answer: `outcome` stays empty, which is already what
 * it means for a failure this library did not produce. MEASURED by BK6.
 */
function oursToLabel(failure: unknown): failure is HttpError {
  try {
    return isHttpError(failure);
  } catch {
    return false;
  }
}

/**
 * Cancels what an attempt produced after the deadline already answered for it.
 *
 * The attempt keeps running when the deadline wins the race, and whatever it
 * produces then belongs to nobody: the caller has its timeout. A transport that
 * ignored the signal still answers eventually, and an abandoned stream holds its
 * connection open until it is collected -- the same reason `deliverFailure`
 * cancels one (ER46, TO37).
 *
 * BOTH outcomes leak, which is why `heldRaw` is read as well. A late SUCCESS
 * carries its stream in the result. A late raw FAILURE leaves it in the call's
 * `rawFailure`, which the loop writes only after the call has already failed, so
 * `deliverFailure` ran with that field still empty and nothing downstream passes
 * by again (TO38).
 *
 * `lost` is read at settle time rather than passed by value, because it is the
 * DEADLINE that knows it won. MEASURED why that matters: a version where the
 * attempt cleared a flag of its own cleared it before this could ever read it --
 * two `.then` chains on one promise run in registration order -- and cancelled
 * nothing at all, in either direction.
 */
function discardWhenLost(
  attempt: Promise<UntracedResult<unknown>>,
  lost: () => boolean,
  heldRaw: () => UntracedResult<unknown> | undefined,
): void {
  const discard = (result?: UntracedResult<unknown>): void => {
    if (!lost()) return;
    discardStream(result);
    discardStream(heldRaw());
  };
  void attempt.then(
    result => discard(result),
    () => discard(),
  );
}

/* ---------------------------------------------------------------------------
 * The overload set. Raw calls split on HEAD and context. Decoded calls also
 * split on empty-response and HTTP-error delivery, preserving their body type.
 *
 * The CONTEXT-carrying signatures come first. Overloads are tried in order and
 * an inline handler is typed by the signature that matches, so behind the
 * context-free pair an inline `before`/`after` would be checked against options
 * that have no `context` at all, and `o.context` would never get a type.
 *
 * Within each group the HEAD signature comes first, because its options are the
 * narrower ones. That ordering is belt-and-braces rather than load-bearing, and
 * MEASURED: swapping the two raw signatures in both groups changes nothing this
 * package checks. `RawNonHeadRequestOptions` is built on `NonHeadBodyAxis`,
 * which simply omits `HeadAxis`, so a HEAD call cannot reach the general raw
 * signature to begin with. The guarantee — that a HEAD answer never gets a
 * result advertising a `stream` it can never carry — lives in the options
 * (DECISION 3.6). The order states it a second time where a reader of this set
 * will look for it.
 *
 * The decoded signatures are where `T` comes from, and there are two
 * ways for a caller to say it, in rising strength:
 *
 *   request<Report>({ url })                              claimed, unchecked
 *   request({ url, validate: { response: ReportSchema } }) claimed and checked
 *
 * A `deserializer` carries its own `T` the same way. Say nothing and the answer
 * is `unknown`, which is the honest report of what is known about a body nobody
 * described.
 *
 * The return type has to depend on the options, and this is why. A single
 * signature answering the union of all result shapes gives `body: T |
 * undefined`, because `RawRequestResult.body` is `undefined` and nothing in the
 * union tells the compiler which member came back. Narrowing does not rescue it
 * either: `RawHeadRequestResult` deliberately carries no `stream` (see its own
 * docs), so it has no discriminant against the decoded result. MEASURED:
 * `'stream' in result` leaves `T | undefined` behind.
 *
 * The raw signatures carry no `T`: a raw call decodes nothing, so there is no
 * result type to infer and a type argument on one would be meaningless. The
 * context-carrying decoded signature takes `<T, C>` with NO defaults, which is
 * what makes `request<Report>({ url, context })` fall off the set instead of
 * quietly landing here with an inferred `C`.
 * ------------------------------------------------------------------------- */

/**
 * One when this attempt rode a flight's request rather than sending its own.
 *
 * The attempt's own sent headers are the whole test: they are set beside the
 * counter at the transport and cleared at the start of each attempt, so their
 * absence says this attempt has no wire of its own. An opener, or an attempt
 * that already sent, adds nothing and keeps the count it earned.
 *
 * A number rather than a boolean because both call sites add it, and because a
 * `?:` at either of them costs the enclosing function a branch it has no room
 * for (`backstage-developer-portal-3t84`).
 */
function ridesFlight(ownWire: Headers | undefined): 0 | 1 {
  return ownWire === undefined ? 1 : 0;
}

/**
 * The kept answer when there is one, and otherwise the flight. A kept answer
 * goes through the same per-caller work as a flight's, so it is handed over
 * the same way.
 */
function keptOr(
  cached: Exchange | undefined,
  board: () => Promise<Exchange>,
): Promise<Exchange> {
  return cached === undefined ? board() : Promise.resolve(cached);
}

/**
 * Where an attempt's answer came from: a kept one, another caller's request,
 * or its own wire. `ownWire` is this attempt's sent headers, and an attempt
 * that rode a flight has none of its own.
 */
function originOf(
  cached: Exchange | undefined,
  ownWire: Headers | undefined,
): AnswerOrigin {
  if (cached !== undefined) return 'cache';
  return ownWire === undefined ? 'flight' : 'transport';
}

/**
 * What a caller that JOINED adopts from a flight that failed after sending.
 *
 * Returns the headers to report, or nothing when there is nothing to adopt. The
 * id and the request COUNT travel together, and this is the one place that
 * decides WHETHER: both key off this single answer, so they cannot diverge.
 * Reporting one without the other says two things at once, because `CO69`
 * reads `requests: 0` as "nothing was sent": an id beside a zero names a
 * request the same log line claims never happened (CO68).
 *
 * `ownWire` is the caller's own sent headers, and it is cleared at the START of
 * each attempt while the request counter accumulates over the whole CALL. So
 * `undefined` here means this ATTEMPT has no wire of its own, not that the call
 * sent nothing, which is why the caller ADDS the one rather than assigning it.
 * Whether a call can reach this with a non-zero counter is not measured; the
 * shape allows it and assignment would lose that request.
 */
function adoptedFromFlight(
  onTheWire: string | undefined,
  ownWire: Headers | undefined,
): Headers | undefined {
  // THE ATTEMPT, not the caller: a caller holding its own wire keeps it.
  if (ownWire !== undefined) return undefined;
  // Nothing went out, so there is nothing to name (CO69).
  if (onTheWire === undefined) return undefined;
  return new Headers({ 'x-request-id': onTheWire });
}

/**
 * A raw HEAD carrying a context.
 *
 * @param options `raw: true`, `method: 'HEAD'` and a `context`. The decoded-only
 *   options are not merely absent but pinned to `never`, so a spread that
 *   carries one in is rejected rather than silently ignored.
 * @returns `status`, `statusText`, the headers, and a `body` that is always
 *   `undefined` so the union has one field to destructure. `ok` carries the
 *   transport's success flag. There is no `stream`: a HEAD
 *   answer has no body to hand over, and leaving that field out is what stops a
 *   caller reaching for one that can never be there.
 * @typeParam C The type of `context`, which reaches `PolicyContext.context` and
 *   every handler on this call typed rather than as `unknown`.
 * @throws Never synchronously. Every failure arrives as a rejected promise,
 *   including one raised before the first `await`.
 *
 *   Every failure the LIBRARY raises on a raw call is an `HttpError` EXCEPT the
 *   two policy refusals: `breaker` refuses with a `CircuitOpenError` and
 *   `bulkhead` with a `BulkheadRejectedError`. Both come from `@ns/resilience`
 *   and answer `isPolicyError` rather than `isHttpError`. Code you supplied does
 *   not follow that rule, so the three cases are worth knowing before you branch
 *   on the class.
 *
 *   Three callbacks reach you as the very object they threw, with `isHttpError`
 *   `false`: `before`, a `policy`, and the function form of `request.trace`.
 *
 *   A callback the library asked for a VALUE is wrapped, because not getting one
 *   is the library's own failure to classify: `serializer` becomes
 *   `UnsupportedBodyError`, and `fetch` becomes `NetworkError`.
 *
 *   Two are neither. A throwing `retry.shouldRetry` is read as "do not retry"
 *   and a throwing `throwOnError` predicate as "throw", so what you catch is the
 *   upstream failure and not your own error -- but yours is not lost: it is
 *   attached at the end of that failure's `cause` chain and it reaches
 *   `logFields()`. An `after` handler IS discarded, and the call still succeeds.
 *
 *   Raw does not decode or validate: `coalesce`, `deserializer`, `validate` and
 *   `maxResponseBytes` are pinned to `never` on this signature, so
 *   `ResponseTooLargeError`, `ResponseDecodeError` and the validation errors do
 *   not occur.
 *
 *   On this raw signature, what the library raises is one of:
 *
 *   - `UnsupportedRequestError` when the call itself does not hold up, or
 *     `UnsupportedBodyError` when the body cannot be serialized. Nothing was
 *     sent in either case.
 *   - `CircuitOpenError` or `BulkheadRejectedError` when a policy held it back.
 *     Neither is an `HttpError`; both answer `isPolicyError`. A `throttle` waits
 *     rather than refusing, so it has no error of its own.
 *   - `RequestTimeoutError` for our own deadline, `RequestAbortError` for the
 *     caller's, and `AttemptTimeoutError` when `retry.perAttemptTimeoutMs` runs out
 *     on the last attempt.
 *   - `NetworkError` when the transport never came back with a status.
 *   - `HttpRequestError` on a status treated as a failure, or the named child
 *     for that status: `BadRequestError` (400), `UnauthorizedError` (401),
 *     `ForbiddenError` (403), `NotFoundError` (404), `MethodNotAllowedError`
 *     (405), `UnprocessableContentError` (422), `HttpThrottleError` (429) and
 *     `ServerError` for any 5xx. `throwOnError: false` delivers these as a
 *     result instead.
 */
export function request<C>(
  options: RawHeadRequestOptionsWithContext<C>,
): Promise<RawHeadRequestResult>;
/**
 * Any other raw call carrying a context: the unread stream, decoded by nobody.
 *
 * @param options `raw: true` with any method other than HEAD, plus a `context`.
 * @returns The response with its `stream` still unread, and reading it is then
 *   yours to finish or to cancel. `maxResponseBytes` is not merely inactive on
 *   this route but unrepresentable, because nothing counts the bytes for you;
 *   bound the read with your own `signal` instead.
 * @typeParam C The type of `context`, as on the HEAD signature above.
 * @throws Never synchronously. Every failure arrives as a rejected promise,
 *   including one raised before the first `await`.
 *
 *   Every failure the LIBRARY raises on a raw call is an `HttpError` EXCEPT the
 *   two policy refusals: `breaker` refuses with a `CircuitOpenError` and
 *   `bulkhead` with a `BulkheadRejectedError`. Both come from `@ns/resilience`
 *   and answer `isPolicyError` rather than `isHttpError`. Code you supplied does
 *   not follow that rule, so the three cases are worth knowing before you branch
 *   on the class.
 *
 *   Three callbacks reach you as the very object they threw, with `isHttpError`
 *   `false`: `before`, a `policy`, and the function form of `request.trace`.
 *
 *   A callback the library asked for a VALUE is wrapped, because not getting one
 *   is the library's own failure to classify: `serializer` becomes
 *   `UnsupportedBodyError`, and `fetch` becomes `NetworkError`.
 *
 *   Two are neither. A throwing `retry.shouldRetry` is read as "do not retry"
 *   and a throwing `throwOnError` predicate as "throw", so what you catch is the
 *   upstream failure and not your own error -- but yours is not lost: it is
 *   attached at the end of that failure's `cause` chain and it reaches
 *   `logFields()`. An `after` handler IS discarded, and the call still succeeds.
 *
 *   Raw does not decode or validate: `coalesce`, `deserializer`, `validate` and
 *   `maxResponseBytes` are pinned to `never` on this signature, so
 *   `ResponseTooLargeError`, `ResponseDecodeError` and the validation errors do
 *   not occur.
 *
 *   On this raw signature, what the library raises is one of:
 *
 *   - `UnsupportedRequestError` when the call itself does not hold up, or
 *     `UnsupportedBodyError` when the body cannot be serialized. Nothing was
 *     sent in either case.
 *   - `CircuitOpenError` or `BulkheadRejectedError` when a policy held it back.
 *     Neither is an `HttpError`; both answer `isPolicyError`. A `throttle` waits
 *     rather than refusing, so it has no error of its own.
 *   - `RequestTimeoutError` for our own deadline, `RequestAbortError` for the
 *     caller's, and `AttemptTimeoutError` when `retry.perAttemptTimeoutMs` runs out
 *     on the last attempt.
 *   - `NetworkError` when the transport never came back with a status.
 *   - `HttpRequestError` on a status treated as a failure, or the named child
 *     for that status: `BadRequestError` (400), `UnauthorizedError` (401),
 *     `ForbiddenError` (403), `NotFoundError` (404), `MethodNotAllowedError`
 *     (405), `UnprocessableContentError` (422), `HttpThrottleError` (429) and
 *     `ServerError` for any 5xx. `throwOnError: false` delivers these as a
 *     result instead.
 */
export function request<C>(
  options: RawNonHeadRequestOptionsWithContext<C>,
): Promise<RawRequestResult>;
/**
 * A decoded call allowing an empty body and rejecting non-ok statuses.
 *
 * @param options `allowEmptyResponse: true`, with `throwOnError` absent or true. A context is required.
 * @returns A successful result with an optional body. Non-ok statuses reject.
 * @typeParam T The decoded body type.
 * @typeParam C The caller's context type.
 * @throws Never synchronously. Every failure arrives as a rejected promise.
 *   Which failure classes, and when, is listed on {@link Request}.
 */
export function request<T, C>(
  options: OptionalBodyRequestOptionsWithContext<T, C> & ThrowOnErrorOptions,
): Promise<DecodedRequestResult<T | undefined>>;
/**
 * A decoded call. The everyday form: an absent body rejects with
 * `EmptyResponseError` and a non-ok status rejects. Both type arguments or
 * neither.
 *
 * @param options `allowEmptyResponse` absent or `false` (written out as
 *   `false` when `T` admits `undefined`), a `context`, and `throwOnError`
 *   absent or true. This group is offered FIRST on purpose:
 *   only here does an inline `before: [o => o.context.key]` get a contextual
 *   type, and behind the context-free pair it would fall back to an implicit
 *   `any`.
 * @returns The decoded body alongside the status and the headers. The body is
 *   never `undefined`.
 * @typeParam T The decoded body type. Three sources supply it and they share one
 *   parameter, so two that disagree are a compile error: a bare type argument
 *   (claimed, unchecked), a `validate.response` (claimed and checked), or a
 *   `deserializer`.
 * @typeParam C The type of `context`. Neither parameter has a default, which is
 *   what makes `request<Report>({ url, context })` fall off the overload set
 *   instead of landing here with an inferred `C`.
 * @throws Never synchronously. Every failure arrives as a rejected promise.
 *   Which failure classes, and when, is listed on {@link Request}.
 */
export function request<T, C>(
  options: RequiredBodyRequestOptionsWithContext<T, C> & ThrowOnErrorOptions,
): Promise<DecodedRequestResult<Exclude<T, undefined>>>;
/**
 * A decoded call allowing empty bodies and status-error delivery.
 *
 * @param options `allowEmptyResponse: true`, with any supported `throwOnError` mode. A context is required.
 * @returns An optional body on success, or an unchecked failure body. Narrow on `ok`.
 * @typeParam T The decoded body type.
 * @typeParam C The caller's context type.
 * @throws Never synchronously. Every failure arrives as a rejected promise.
 *   Which failure classes, and when, is listed on {@link Request}.
 */
export function request<T, C>(
  options: OptionalBodyRequestOptionsWithContext<T, C>,
): Promise<DecodedRequestResult<T | undefined> | DecodedFailureResult>;
/**
 * A decoded call requiring a body and allowing status-error delivery.
 *
 * @param options An absent or false `allowEmptyResponse` (written out as
 *   `false` when `T` admits `undefined`), with any supported `throwOnError`
 *   mode. A context is required.
 * @returns A required body on success, or an unchecked failure body. Narrow on `ok`.
 * @typeParam T The decoded body type.
 * @typeParam C The caller's context type.
 * @throws Never synchronously. Every failure arrives as a rejected promise.
 *   Which failure classes, and when, is listed on {@link Request}.
 */
export function request<T, C>(
  options: RequiredBodyRequestOptionsWithContext<T, C>,
): Promise<DecodedRequestResult<Exclude<T, undefined>> | DecodedFailureResult>;
/**
 * A decoded call with dynamic empty-body permission and rejecting non-ok statuses.
 *
 * @param options A boolean `allowEmptyResponse`, with `throwOnError` absent or true. A context is required.
 *   When `T` admits `undefined`, `allowEmptyResponse` has to be present: an omitted or `undefined` value is not a choice.
 * @returns A successful result with an optional body. Non-ok statuses reject.
 * @typeParam T The decoded body type.
 * @typeParam C The caller's context type.
 * @throws Never synchronously. Every failure arrives as a rejected promise.
 *   Which failure classes, and when, is listed on {@link Request}.
 */
export function request<T, C>(
  options: ExplicitChoiceGate<
    DecodedRequestOptionsWithContext<T, C>,
    NoInfer<T>
  > &
    ThrowOnErrorOptions,
): Promise<DecodedRequestResult<T | undefined>>;
/**
 * A decoded call with dynamic body and status-error options.
 *
 * @param options Decoded options whose boolean values are not known statically. A context is required.
 *   When `T` admits `undefined`, `allowEmptyResponse` has to be present: an omitted or `undefined` value is not a choice.
 * @returns An optional body on success, or an unchecked failure body.
 * @typeParam T The decoded body type.
 * @typeParam C The caller's context type.
 * @throws Never synchronously. Every failure arrives as a rejected promise.
 *   Which failure classes, and when, is listed on {@link Request}.
 */
export function request<T, C>(
  options: ExplicitChoiceGate<
    DecodedRequestOptionsWithContext<T, C>,
    NoInfer<T>
  >,
): Promise<DecodedRequestResult<T | undefined> | DecodedFailureResult>;
/**
 * A raw HEAD.
 *
 * @param options `raw: true` and `method: 'HEAD'`, without a `context`.
 * @returns `status`, `statusText`, the headers and an always-`undefined` `body`,
 *   with `ok` but no `stream` -- the same shape and the same reason as the
 *   context-carrying HEAD above.
 * @throws Never synchronously. Every failure arrives as a rejected promise,
 *   including one raised before the first `await`.
 *
 *   Every failure the LIBRARY raises on a raw call is an `HttpError` EXCEPT the
 *   two policy refusals: `breaker` refuses with a `CircuitOpenError` and
 *   `bulkhead` with a `BulkheadRejectedError`. Both come from `@ns/resilience`
 *   and answer `isPolicyError` rather than `isHttpError`. Code you supplied does
 *   not follow that rule, so the three cases are worth knowing before you branch
 *   on the class.
 *
 *   Three callbacks reach you as the very object they threw, with `isHttpError`
 *   `false`: `before`, a `policy`, and the function form of `request.trace`.
 *
 *   A callback the library asked for a VALUE is wrapped, because not getting one
 *   is the library's own failure to classify: `serializer` becomes
 *   `UnsupportedBodyError`, and `fetch` becomes `NetworkError`.
 *
 *   Two are neither. A throwing `retry.shouldRetry` is read as "do not retry"
 *   and a throwing `throwOnError` predicate as "throw", so what you catch is the
 *   upstream failure and not your own error -- but yours is not lost: it is
 *   attached at the end of that failure's `cause` chain and it reaches
 *   `logFields()`. An `after` handler IS discarded, and the call still succeeds.
 *
 *   Raw does not decode or validate: `coalesce`, `deserializer`, `validate` and
 *   `maxResponseBytes` are pinned to `never` on this signature, so
 *   `ResponseTooLargeError`, `ResponseDecodeError` and the validation errors do
 *   not occur.
 *
 *   On this raw signature, what the library raises is one of:
 *
 *   - `UnsupportedRequestError` when the call itself does not hold up, or
 *     `UnsupportedBodyError` when the body cannot be serialized. Nothing was
 *     sent in either case.
 *   - `CircuitOpenError` or `BulkheadRejectedError` when a policy held it back.
 *     Neither is an `HttpError`; both answer `isPolicyError`. A `throttle` waits
 *     rather than refusing, so it has no error of its own.
 *   - `RequestTimeoutError` for our own deadline, `RequestAbortError` for the
 *     caller's, and `AttemptTimeoutError` when `retry.perAttemptTimeoutMs` runs out
 *     on the last attempt.
 *   - `NetworkError` when the transport never came back with a status.
 *   - `HttpRequestError` on a status treated as a failure, or the named child
 *     for that status: `BadRequestError` (400), `UnauthorizedError` (401),
 *     `ForbiddenError` (403), `NotFoundError` (404), `MethodNotAllowedError`
 *     (405), `UnprocessableContentError` (422), `HttpThrottleError` (429) and
 *     `ServerError` for any 5xx. `throwOnError: false` delivers these as a
 *     result instead.
 */
export function request(
  options: RawHeadRequestOptions,
): Promise<RawHeadRequestResult>;
/**
 * Any other raw call.
 *
 * @param options `raw: true` with any method other than HEAD, without a
 *   `context`.
 * @returns The response with its `stream` still unread, yours to finish or to
 *   cancel.
 * @throws Never synchronously. Every failure arrives as a rejected promise,
 *   including one raised before the first `await`.
 *
 *   Every failure the LIBRARY raises on a raw call is an `HttpError` EXCEPT the
 *   two policy refusals: `breaker` refuses with a `CircuitOpenError` and
 *   `bulkhead` with a `BulkheadRejectedError`. Both come from `@ns/resilience`
 *   and answer `isPolicyError` rather than `isHttpError`. Code you supplied does
 *   not follow that rule, so the three cases are worth knowing before you branch
 *   on the class.
 *
 *   Three callbacks reach you as the very object they threw, with `isHttpError`
 *   `false`: `before`, a `policy`, and the function form of `request.trace`.
 *
 *   A callback the library asked for a VALUE is wrapped, because not getting one
 *   is the library's own failure to classify: `serializer` becomes
 *   `UnsupportedBodyError`, and `fetch` becomes `NetworkError`.
 *
 *   Two are neither. A throwing `retry.shouldRetry` is read as "do not retry"
 *   and a throwing `throwOnError` predicate as "throw", so what you catch is the
 *   upstream failure and not your own error -- but yours is not lost: it is
 *   attached at the end of that failure's `cause` chain and it reaches
 *   `logFields()`. An `after` handler IS discarded, and the call still succeeds.
 *
 *   Raw does not decode or validate: `coalesce`, `deserializer`, `validate` and
 *   `maxResponseBytes` are pinned to `never` on this signature, so
 *   `ResponseTooLargeError`, `ResponseDecodeError` and the validation errors do
 *   not occur.
 *
 *   On this raw signature, what the library raises is one of:
 *
 *   - `UnsupportedRequestError` when the call itself does not hold up, or
 *     `UnsupportedBodyError` when the body cannot be serialized. Nothing was
 *     sent in either case.
 *   - `CircuitOpenError` or `BulkheadRejectedError` when a policy held it back.
 *     Neither is an `HttpError`; both answer `isPolicyError`. A `throttle` waits
 *     rather than refusing, so it has no error of its own.
 *   - `RequestTimeoutError` for our own deadline, `RequestAbortError` for the
 *     caller's, and `AttemptTimeoutError` when `retry.perAttemptTimeoutMs` runs out
 *     on the last attempt.
 *   - `NetworkError` when the transport never came back with a status.
 *   - `HttpRequestError` on a status treated as a failure, or the named child
 *     for that status: `BadRequestError` (400), `UnauthorizedError` (401),
 *     `ForbiddenError` (403), `NotFoundError` (404), `MethodNotAllowedError`
 *     (405), `UnprocessableContentError` (422), `HttpThrottleError` (429) and
 *     `ServerError` for any 5xx. `throwOnError: false` delivers these as a
 *     result instead.
 */
export function request(
  options: RawNonHeadRequestOptions,
): Promise<RawRequestResult>;
/**
 * A decoded call allowing an empty body and rejecting non-ok statuses.
 *
 * @param options `allowEmptyResponse: true`, with `throwOnError` absent or true.
 * @returns A successful result with an optional body. Non-ok statuses reject.
 * @typeParam T The decoded body type.
 * @throws Never synchronously. Every failure arrives as a rejected promise.
 *   Which failure classes, and when, is listed on {@link Request}.
 */
export function request<T = unknown>(
  options: OptionalBodyRequestOptions<T> & ThrowOnErrorOptions,
): Promise<DecodedRequestResult<T | undefined>>;
/**
 * A decoded call. The everyday form: an absent body rejects with
 * `EmptyResponseError` and a non-ok status rejects.
 *
 * @param options A `url` and `allowEmptyResponse` absent or `false` (written
 *   out as `false` when `T` admits `undefined`), with `throwOnError` absent
 *   or true. The object is never mutated, so it may be reused or frozen.
 * @returns The decoded body alongside the status and the headers. The body is
 *   never `undefined`.
 * @typeParam T The decoded body type, from a type argument, a
 *   `validate.response` or a `deserializer`. Say nothing and it is `unknown`,
 *   which is the honest report of a body nobody described.
 * @throws Never synchronously. Every failure arrives as a rejected promise.
 *   Which failure classes, and when, is listed on {@link Request}.
 */
export function request<T = unknown>(
  options: RequiredBodyRequestOptions<T> & ThrowOnErrorOptions,
): Promise<DecodedRequestResult<Exclude<T, undefined>>>;
/**
 * A decoded call allowing empty bodies and status-error delivery.
 *
 * @param options `allowEmptyResponse: true`, with any supported `throwOnError` mode.
 * @returns An optional body on success, or an unchecked failure body. Narrow on `ok`.
 * @typeParam T The decoded body type.
 * @throws Never synchronously. Every failure arrives as a rejected promise.
 *   Which failure classes, and when, is listed on {@link Request}.
 */
export function request<T = unknown>(
  options: OptionalBodyRequestOptions<T>,
): Promise<DecodedRequestResult<T | undefined> | DecodedFailureResult>;
/**
 * A decoded call requiring a body and allowing status-error delivery.
 *
 * @param options An absent or false `allowEmptyResponse` (written out as
 *   `false` when `T` admits `undefined`), with any supported `throwOnError`
 *   mode.
 * @returns A required body on success, or an unchecked failure body. Narrow on `ok`.
 * @typeParam T The decoded body type.
 * @throws Never synchronously. Every failure arrives as a rejected promise.
 *   Which failure classes, and when, is listed on {@link Request}.
 */
export function request<T = unknown>(
  options: RequiredBodyRequestOptions<T>,
): Promise<DecodedRequestResult<Exclude<T, undefined>> | DecodedFailureResult>;
/**
 * A decoded call with dynamic empty-body permission and rejecting non-ok statuses.
 *
 * @param options A boolean `allowEmptyResponse`, with `throwOnError` absent or true.
 *   When `T` admits `undefined`, `allowEmptyResponse` has to be present: an omitted or `undefined` value is not a choice.
 * @returns A successful result with an optional body. Non-ok statuses reject.
 * @typeParam T The decoded body type.
 * @throws Never synchronously. Every failure arrives as a rejected promise.
 *   Which failure classes, and when, is listed on {@link Request}.
 */
export function request<T = unknown>(
  options: ExplicitChoiceGate<DecodedRequestOptions<T>, NoInfer<T>> &
    ThrowOnErrorOptions,
): Promise<DecodedRequestResult<T | undefined>>;
/**
 * A decoded call with dynamic body and status-error options.
 *
 * @param options Decoded options whose boolean values are not known statically.
 *   When `T` admits `undefined`, `allowEmptyResponse` has to be present: an omitted or `undefined` value is not a choice.
 * @returns An optional body on success, or an unchecked failure body.
 * @typeParam T The decoded body type.
 * @throws Never synchronously. Every failure arrives as a rejected promise.
 *   Which failure classes, and when, is listed on {@link Request}.
 */
export function request<T = unknown>(
  options: ExplicitChoiceGate<DecodedRequestOptions<T>, NoInfer<T>>,
): Promise<DecodedRequestResult<T | undefined> | DecodedFailureResult>;

/**
 * Assembles the call and hands it to the transport.
 *
 * @remarks
 * Every step that can reject the call runs BEFORE the transport is CALLED, so
 * a rejected call has provably sent nothing. A runtime with no `fetch` at all
 * is refused there too, rather than discovered by calling `undefined` and
 * reported as a network failure.
 *
 * @privateRemarks
 * The implementation signature is never seen by a caller, so it may be the wide
 * one the body actually produces. That is what lets the overloads stand without
 * a single assertion.
 */
export async function request(
  options: RequestOptions,
): Promise<RequestResult<unknown>> {
  // THE FIRST TWO LINES OF THE CALL, and that placement is ER67. Everything
  // this call can fail on is below them, so a refusal from `resolveAddress` --
  // the earliest throw there is -- still reports which call it was and how long
  // it took.
  //
  // Two unconditional costs per call, and the second is the larger one.
  // MEASURED on Node 22, 200 000 iterations: `Date.now()` is 49 ns and
  // `newCallId()` is 1364 ns, so the id is 28x the clock. Neither can be made
  // conditional -- both belong on a failure, and a failure happens with
  // debugging off -- and 1.4 us against an HTTP call is nothing. The number is
  // here so that a later reader auditing the cost of ER67 does not audit the
  // cheaper of the two lines.
  //
  // `startCallLog` is handed this same `startedAt` rather than starting its own
  // clock, so the debug line and the failure cannot report two durations for
  // one call.
  const startedAt = Date.now();
  const callId = newCallId();
  // Per call, not per attempt, and they part company as soon as an attempt
  // strands before the transport: a throwing `before`, a failed serialization, a
  // refused limit, a spent deadline or an open circuit (`ER71`).
  let attemptsStarted = 0;
  let requestsSent = 0;
  // Read off the caller's own options, so every handler on every attempt is told
  // what the CALLER asked rather than what its predecessor left (`TR23`).
  const beforeTrace = beforeTraceOf(options, callId);
  // The headers THIS attempt handed the transport, or nothing when it never got
  // there. Everything reported about the wire is read back off these (`TR27`).
  let sentHeaders: Headers | undefined;
  const counted = (): { attempts: number; requests: number } => ({
    attempts: attemptsStarted,
    requests: requestsSent,
  });
  /** What this call knows about itself right now. One builder, so a failure and
   * a result cannot describe the same call differently (`ER72`). */
  const traceNow = (): Trace =>
    traceOf(beforeTrace, counted(), sentHeaders, startedAt);

  // Minted HERE, per call, and not inside `run`, which is the per-attempt
  // unit. A retry is one operation, so its attempts are sibling spans under
  // one trace rather than three traces a viewer cannot relate (TR19).
  const traceId = randomHex(16);

  /**
   * The id that actually went on the wire, once one has (ER64).
   *
   * Mutable and read by `madeHere` below, because it is not known when that
   * closure is built: the headers are settled a hundred lines down. A failure
   * raised before then carries no `requestId`, which is the truth -- nothing had
   * gone out to name.
   */
  let requestId: string | undefined;

  /**
   * The bag a failure raised by THIS call should carry.
   *
   * A function and not a value, because `durationMs` is read when the failure
   * is built rather than when the pipeline passed here. The two are far apart
   * on the paths that matter: a body read that trips the cap can run for
   * hundreds of milliseconds after the read began.
   *
   * ABSENT OPTIONS TAKE THE ID WITH THEM, and that is ER70 rather than a
   * shortcut. `undefined` here means the failure is being raised inside the
   * shared region, where it belongs to no one in particular -- see
   * `attributable`. An id put on it would name whoever opened the flight and be
   * read by whoever caught it, which is the same crossing `options` is dropped
   * to prevent. Every participant is handed a rebuilt failure carrying its own
   * (CO59), so nothing is lost by leaving it off here.
   */
  const madeHere = (options: RequestOptions | undefined): HttpErrorOptions =>
    options === undefined
      ? {}
      : {
          call: traceNow(),
          options,
        };

  // Read ONCE, off the caller's own options, before the headers are merged and
  // before any handler has run. The call's own `before` list runs ahead of the
  // process-wide one, so a handler that set the option here would still be
  // skipping the global handlers behind it while the global headers had been
  // merged a step earlier — one question with three answers. This reading is
  // what keeps them the same one (IS20).
  const isolated = options.isolate === true;

  // DB1: opened HERE, so the duration it reports is the one the caller waited
  // -- everything below is inside it. One line per CALL and not per attempt:
  // the retries have their own namespace (DB9), and this fires in the `finally`
  // that every exit passes through.
  const callLog = startCallLog(callId, startedAt);

  // What the observers are handed. It is replaced at every point where the
  // pipeline produces a new version, so an `after` that fires BECAUSE a `before`
  // threw (AF14) still sees the options as they stood when it did.
  //
  // And what a failure raised OUTSIDE an attempt carries as `HttpError.options`
  // -- the deadline firing while a `before` handler is still running is the case
  // (TO28). ER10 and RY21 ask for the DEFINITIVE options, and from the moment
  // `runBefore` returns that is exactly what this holds. Before that there is no
  // `final` yet, and no requirement says what to do; the choice made here is the
  // one `method` and `url` above already make -- the best known so far, which
  // until the handlers have run is what the caller wrote. `undefined` would be
  // the worse answer: it says nothing where the caller's own options are true,
  // and a reader cannot tell that absence from a field nobody filled in.
  let observedOptions: RequestOptions = options;
  const outcome: Outcome = { body: undefined };

  // The result of a raw call whose status turned out to be a failure, kept until
  // the delivery decision below can say who it belongs to. `undefined` on every
  // other call: a decoded result is rebuilt from the failure, and a successful
  // one never comes past here. It holds the LATEST attempt only -- see
  // `discardRawFailure` for what happens to the ones before it.
  let rawFailure: UntracedResult<unknown> | undefined;

  // The best known so far, and mutable for that reason. The deadline may fire
  // while a `before` handler is still running, so the failure it reports has to
  // be buildable before the address has been resolved (TO28).
  let method = options.method ?? 'GET';
  let url = options.url;

  /**
   * The retry config as it stood before anything could reach it, and the one the
   * attempts run on (BE43).
   *
   * A COPY, because `retryConfigOf` hands back the caller's own object and a
   * `before` handler holding a reference to it can write into it mid-call. That
   * write used to land in the loop: MEASURED, a handler setting `maxAttempts`
   * to 1 cut three attempts to one. The copy is also the evidence the refusal
   * needs -- see `refuseHandlerRewrites`.
   *
   * A `Policy` is passed through as itself. There are no fields on a function to
   * copy, and copying would break the identity `guarded` composes on.
   */
  const retryAtEntry: RetryOption =
    options.retry === undefined || typeof options.retry === 'function'
      ? options.retry
      : { ...options.retry };

  /**
   * The policy list as it stood before the first handler ran.
   *
   * A shallow copy, for the reason `retryAtEntry` above gives: the refusal
   * compares CONTENTS, and a handler that pushes into the caller's own array
   * keeps the reference, so a comparison against the live array would always
   * find the two equal.
   */
  const policiesAtEntry: readonly Policy[] | undefined =
    options.policies === undefined ? undefined : [...options.policies];

  const deadline = startDeadline(options.timeout, options.signal);

  /**
   * The two classes a caller branches on, from the one thing that separates
   * them. Both arrive as an abort; only whose abort it was decides the class.
   *
   * `madeWith` is REQUIRED rather than defaulted, and that is deliberate: this
   * is called from inside the shared region and from outside it, and those two
   * places may not carry the same options (see `attributable`). A default would
   * let a new call site inherit an answer instead of giving one.
   */
  const failureFor = (
    by: EndedBy,
    madeWith: RequestOptions | undefined,
  ): HttpError =>
    by === 'deadline'
      ? new RequestTimeoutError(method, url, madeHere(madeWith))
      : new RequestAbortError(method, url, madeHere(madeWith));

  /** The same failure asked for synchronously, or `undefined` while the call runs. */
  const signalFailure = (
    madeWith: RequestOptions | undefined,
  ): HttpError | undefined => {
    const endedBy = deadline.endedBy();
    return endedBy === undefined ? undefined : failureFor(endedBy, madeWith);
  };

  /**
   * The clock ONE attempt runs on, held here because the attempt that arms it
   * and the transport call that runs under it are three functions apart.
   *
   * `undefined` while nothing is bounded, which is every call that named no
   * `perAttemptTimeoutMs` and every call whose `retry` is a `Policy` (RC55). A
   * caller who did not ask for a per-attempt bound gets exactly the signal they
   * got before this existed.
   */
  let attemptClock:
    | { expired: () => boolean; signal: AbortSignal; stop: () => void }
    | undefined;

  /**
   * Arms the clock for the attempt about to start, or nothing to arm.
   *
   * THE BUDGET IS `min(perAttemptTimeoutMs, what is left)`, WORKED OUT NOW. Now,
   * and not once for the call: this runs at the START of an attempt, so the
   * wait that preceded it has already been spent and `remaining()` answers
   * with the deadline as it stands (RC74). Working it out once satisfies the
   * first attempt exactly and hands every later one a budget the deadline has
   * already eaten into.
   *
   * IT ABORTS THE SIGNAL THE TRANSPORT HOLDS, rather than racing the attempt
   * against a timer beside it (RC68). A race produces the same timeline to the
   * millisecond while the transport is never told anything, so its socket stays
   * open for the rest of the call -- three attempts against a silent server
   * leave three abandoned connections, which is the very leak the knob exists
   * to close. What the transport then DOES with the abort is its own contract:
   * `globalThis.fetch` drops the connection, a caller's own `FetchFunction` may
   * ignore it. The promise is the signal, not the socket.
   *
   * THE MERGE IS `deadline.ts`'s, NOT A SECOND ONE (RC69), and it carries NO
   * `ended` promise of its own. Reusing `startDeadline` here instead would look
   * tidier and leave one listener behind per attempt through the promise it
   * arms, which is invisible at three attempts and is exactly what RC70
   * measures at fifty.
   */
  const armAttemptClock = (
    config: RetryConfig | undefined,
  ): typeof attemptClock => {
    const perAttemptTimeoutMs = config?.perAttemptTimeoutMs;
    if (perAttemptTimeoutMs === undefined) return undefined;

    const ours = new AbortController();
    const budget = Math.min(perAttemptTimeoutMs, deadline.remaining());
    const reason = new Error(`this attempt's ${budget} ms elapsed`);
    const timer = setTimeout(() => ours.abort(reason), budget);
    const merged = mergeSignals([ours.signal, deadline.signal]);

    return {
      // OURS, never the merged signal. The merged one also carries the
      // deadline and the caller's abort, and reading it would report either of
      // those as an attempt that ran long.
      expired: () => ours.signal.aborted,
      signal: merged.signal,
      stop: () => {
        clearTimeout(timer);
        merged.release();
      },
    };
  };

  /**
   * The attempt clock's half of what `signalFailure` does for the deadline.
   *
   * Asked only AFTER `signalFailure` has answered nothing, which is the whole
   * of the precedence: a call whose own deadline has gone is over, and saying
   * "that attempt took too long, another is coming" about it would be false.
   */
  const attemptFailure = (
    madeWith: RequestOptions | undefined,
  ): HttpError | undefined =>
    attemptClock?.expired() === true
      ? new AttemptTimeoutError(method, url, madeHere(madeWith))
      : undefined;

  /**
   * What ended this attempt from the outside, or nothing while it may still run.
   *
   * The three-way precedence in one place: our own deadline, then the caller's
   * abort (`signalFailure` answers for both), then the attempt's own clock. A
   * call that is over is over, whichever clock happened to notice first, so the
   * attempt clock only ever speaks where the other two say nothing.
   */
  const endedFailure = (
    madeWith: RequestOptions | undefined,
  ): HttpError | undefined =>
    signalFailure(madeWith) ?? attemptFailure(madeWith);

  /**
   * The signal THIS attempt's transport runs on.
   *
   * The attempt's own merged one where a per-attempt bound was asked for, and
   * the call's merged deadline where it was not -- so a caller who named no
   * `perAttemptTimeoutMs` is handed exactly what they were handed before this
   * existed.
   */
  const transportSignal = (): AbortSignal =>
    attemptClock?.signal ?? deadline.signal;

  /**
   * The call itself, as a promise of its own so the deadline can race it.
   *
   * Nothing inside watches a clock, and that is the whole reason for the race:
   * a `before` handler or a `deserializer` that never settles listens to no
   * signal, so only an outer race can end the call it is holding up (TO25, TO28).
   */
  const run = async (): Promise<UntracedResult<unknown>> => {
    // The headers are merged HERE, not just before the transport, and that
    // ordering is the point: the first handler already reads the headers the
    // call goes out with (HE22, HE31), so it can decide against a global instead
    // of against half the picture. It also makes `final` genuinely final (BE5) —
    // after this there is nothing left to merge in behind the handlers.
    //
    // IS16 arrives here as a merge against NO global rather than as a branch
    // around the merge: an isolated call keeps everything `mergeHeaders`
    // promises about the caller's own record, BE31 included.
    const mergedOptions: RequestOptions = {
      ...options,
      headers: mergeHeaders(isolated ? {} : request.headers, options.headers),
    };
    observedOptions = mergedOptions;
    const atEntry = beforeStateOf(mergedOptions, policiesAtEntry, retryAtEntry);

    // SI8 and SI26: the deadline's signal is the MERGED one -- ours and the
    // caller's as a single object (`startDeadline`) -- so a handler learns that
    // the call is over whichever of the two ended it.
    const { final, own } = await runBefore(
      mergedOptions,
      isolated,
      deadline.signal,
      beforeTrace,
    );
    method = final.method ?? 'GET';

    // Among the refusals below and ahead of all of them, because this one asks
    // the cheapest version of their shared question: can this call be made at
    // all. Asked here, it is answered before the caller's own `validate.request`
    // runs, so a call the library turns down has provably run none of the
    // caller's code and touched no transport (UR4).
    refuseHandlerRewrites(mergedOptions, own, final, madeHere(final), atEntry);

    // AFTER the refusals, not before. A refused call is still observed, and it
    // must be observed by the caller's OWN `after` handlers -- handing it to the
    // list a global handler just tried to substitute would let the refusal be
    // reported by the very handlers it refused (`GB16`).
    observedOptions = final;

    // On `final`, so on what the handlers left behind rather than on what the
    // caller wrote (VA14): a handler may rewrite the method, the url or the body,
    // and judging the caller's version would judge something else than what goes
    // out. Placed here so a rejection provably reaches no transport (VA1).
    //
    // `approved` rather than `validated` because this direction throws its
    // result away, so awaiting the validator in full costs nothing here and
    // would cost the body in the other direction (VA20, VA21).
    const outgoing = final.validate?.request;
    if (outgoing !== undefined) {
      await approved(
        outgoing,
        final,
        cause =>
          new RequestValidationError(method, final.url, {
            ...madeHere(final),
            cause,
          }),
      );
    }

    url = rethrowAs(
      () => resolveAddress(final),
      cause =>
        new UnsupportedRequestError(method, final.url, {
          ...madeHere(final),
          cause,
        }),
    );
    // The id is folded in HERE, before the coalescing key is computed, so a
    // caller that names its request is answered by that request and not by one
    // carrying another caller's name. Trace headers go on AFTER the key on
    // purpose (TR14). This one must not (TR20).
    const headers = rethrowAs(
      () => withRequestId(toHeaders(final.headers ?? {}), final.requestId),
      cause =>
        new UnsupportedRequestError(method, url, { ...madeHere(final), cause }),
    );
    // The SECOND reading, on what the handlers left behind (UR21). The first one
    // ran before the loop started, which is what keeps a bad number out of the
    // attempts; this one covers the options as they now stand, for the same
    // reason the validator and the address above are read off `final` (VA14).
    rethrowAs(
      () => validateOptions(final),
      cause =>
        new UnsupportedRequestError(method, url, { ...madeHere(final), cause }),
    );

    // No own transport means the platform's own (FE1), which is the shape
    // almost every call site has. Read HERE and not captured once at module
    // load, because a capture would pin whichever function happened to exist at
    // import time -- an order-of-import decision this library has no business
    // making on the process's behalf. Read off `final` and not off `options`,
    // for the same reason the validator above is (VA14): a `before` handler may
    // supply the transport, and judging the caller's version would judge
    // something else than what the call goes out on.
    //
    // Resolved HERE, among the other refusals, and not at the point of use a
    // hundred lines down. A call with nowhere to send it is the same kind of
    // question as an address that does not resolve (UR4): whether this call can
    // go out at all. Asked here it is answered before the caller's `serialize`
    // runs, so a call this library refuses has provably run none of the
    // caller's code either -- and a serializer that also throws can no longer
    // report `unsupported body` for a call whose real problem is that there is
    // no body to send it with.
    //
    // What this costs is real and accepted rather than guarded against (FE8):
    // in Backstage the global carries none of the auth chain that
    // `fetchApi.fetch` does, so a call that needs a token passes its own.
    const transport = transportFor(final.fetch, request.fetch);
    if (transport === undefined) {
      // `unsupported` and not `network` (FE15). A `NetworkError` says the
      // request left and did not arrive, which sends the next reader to look at
      // the network. Nothing left, and nothing could: this runtime has no fetch
      // to call. That is a question about the runtime, and `unsupported` is
      // where this library files those.
      throw new UnsupportedRequestError(method, url, {
        ...madeHere(final),
        cause: new TypeError(
          'no transport: this runtime has no global `fetch`, and no `fetch` option was given',
        ),
      });
    }

    // The decoded options, bound rather than narrowed again further down. `raw`
    // is what separates the two shapes, and binding the narrowed value here is
    // what lets the exchange below read `deserializer` and `maxResponseBytes`
    // from inside a closure without asking a second time.
    const decodedOptions = final.raw === true ? undefined : final;

    // THE KEY IS COMPUTED WHERE THE SHARED REGION OPENS, which is one line
    // before the serializer runs -- so it reads the body the caller wrote and
    // not the bytes it becomes (CO22, SE11), and it reads the url and the
    // headers as they will go out, so a `before` handler that adds one splits
    // the pools (CO14).
    // Computed BEFORE the key, applied after it. `perTrace` keys on what this
    // call would send, and minting the ids twice would key on a request that
    // never went out (TR15).
    const askedTrace = traceFor(final.trace, request.trace);
    const tracedRequestHeaders = headersForTrace(askedTrace, callId, traceId);

    const flight =
      decodedOptions === undefined
        ? undefined
        : flightOf(decodedOptions, {
            headers,
            method,
            traced: tracedRequestHeaders,
            tracedIsInherited: typeof askedTrace === 'object',
            transport,
            url,
          });

    // AFTER the key, before the wire. Earlier and every traced call would sit
    // in a pool of its own, which is the opposite of what a caller asking for
    // coalescing wants (TR14). Later and the transport would already have the
    // headers it is going to send.
    addTrace(headers, tracedRequestHeaders);
    // AFTER the merge, so this is what the transport will send: the caller's own
    // `x-request-id` wins over anything asked for (TR6), and reading the header
    // rather than `tracedRequestHeaders` is what makes that true here too.
    // LITERALLY what the header carries, unshaped. Normalizing here mapped two
    // different requests onto one id -- `checkout 9` was reported as
    // `checkout9`, which another caller really used -- and a field meant for
    // finding a request back in the server log may not name a different one
    // (ER64).
    requestId = headers.get('x-request-id') ?? undefined;

    // WHAT A FAILURE RAISED INSIDE THE SHARED REGION MAY NAME AS ITS OWN, which
    // is nothing at all once that region is actually shared. Such a failure is
    // ONE object handed to every caller that joined -- `joinFlight` rethrows it
    // untouched, and only a SUCCESS is copied per caller -- so anything on it
    // belongs to whoever opened the flight. `options.headers` is where the
    // bearer token lives (9.9), and handing Alice's token to Bob is the exact
    // crossing DECISION 9.12 splits the default pool to prevent. A named pool
    // keys on the name alone (CO3), so two callers with different tokens DO
    // share one flight there and this is not hypothetical.
    //
    // `undefined` rather than a stripped copy: a half-emptied options record is
    // a shape a caller has to learn, and there is nothing true to put in it.
    //
    // Outside the region every failure belongs to one call and carries `final`,
    // which is what ER10 and RY21 ask for.
    const attributable = flight === undefined ? final : undefined;

    // Awaited here, inside the race, so a `serialize` that never settles ends the
    // call on the deadline with nothing sent (SE14) rather than hanging forever.
    // A serializer that throws is the same failure as a body the built-in
    // transformation cannot write: either way there is no body to send (SE6).
    const wireBody = await rethrowAsAsync(
      () => toWireBody(final.body, final.serializer),
      cause =>
        new UnsupportedBodyError(method, url, { ...madeHere(final), cause }),
    );

    // What the caller wrote wins over the label this call has to offer, and
    // `Headers.has` normalizes the name, so a `Content-Type` written with a
    // capital C loses nothing (SE4, SE10, BA20). The label is the body type's
    // without a serializer and the serializer's with one (SE3).
    if (wireBody.contentType !== undefined && !headers.has('content-type')) {
      headers.set('content-type', wireBody.contentType);
    }

    // Asked here rather than left to the race, because a race settles the
    // caller's promise and leaves the loser running: this pipeline would open
    // the connection anyway, a tick later. That costs a socket, and on a write
    // it can reach the origin for a call that was already cancelled (SI3).
    const cancellation = signalFailure(final);
    if (cancellation !== undefined) throw cancellation;

    /**
     * THE SHARED REGION: the transport call and the decode, and nothing else.
     *
     * That boundary is what decides the fingerprint. Everything outside it --
     * the handlers, the validators, the retry loop, the policies and the
     * delivery choice -- runs per caller, so no option of theirs can reach
     * another caller by accident (RY11, PO10, TE11).
     *
     * WHICH SIGNAL IT RUNS ON IS NOT ITS OWN TO DECIDE, and that is what keeps a
     * caller's cancellation from reaching work that is not theirs. Alone, a call
     * runs on its own merged deadline as it always did. In a pool the flight
     * hands over its own, and one participant leaving no longer ends the request
     * everybody else is waiting for (CO43, CO44).
     */
    const exchange = async (
      transportSignal: AbortSignal,
      // Absent when this call is in no pool, because then there is no flight to
      // tell. A flight passes it always, and this is the line it names: from
      // here on there is a request a server can have seen, which is the one
      // thing a caller that joined may report as its own.
      reachedWire?: () => void,
    ): Promise<Exchange> => {
      requestsSent += 1;
      sentHeaders = headers;
      reachedWire?.();
      const response = await rethrowAsAsync(
        () =>
          transport(url, {
            body: wireBody.body,
            // THE NINE STANDARD FETCH OPTIONS, read from `final` -- the very object
            // the fingerprint reads (`decodedOptions` above IS `final`). One source
            // for both: two calls judged identical on the key cannot then differ on
            // the wire, and two that differ on the wire cannot share a flight.
            //
            // They are handed over uninterpreted, undefaulted and unvalidated. An
            // option the caller did not set stays `undefined` here, and that is what
            // lets the platform apply its own default instead of one this library
            // invented (SF2).
            cache: final.cache,
            credentials: final.credentials,
            // Always 'half': measured, a stream body makes fetch throw without it,
            // and one fixed value beats a condition that has to track the body axis.
            duplex: 'half',
            headers,
            integrity: final.integrity,
            keepalive: final.keepalive,
            method,
            mode: final.mode,
            priority: final.priority,
            redirect: final.redirect,
            referrer: final.referrer,
            referrerPolicy: final.referrerPolicy,
            signal: transportSignal,
          }),
        // WHAT THE WIRE DID, and nothing about who was waiting for it. This
        // region is shared, and only the OPENER's `exchange` ever runs, so the
        // deadline and the caller-signal reachable from here are the opener's.
        // Reading them to decide the CLASS would hand a joiner the opener's
        // abort state -- and since the join rebuilds the failure per caller, it
        // would arrive stamped with the joiner's own id and duration, which is
        // a false statement that looks authoritative (CO64).
        //
        // Whether a failure is an abort is asked once per caller, below, where
        // the rebuild already is.
        cause =>
          new NetworkError(method, url, { ...madeHere(attributable), cause }),
      );

      // Rebuilt rather than handed over, by the one function that does it.
      const observedResponse = observedOf(response);

      // Written down BEFORE the decode, which is what keeps a decode failure
      // reportable: the attempt that could not read the body still tells an
      // `after` handler what the upstream said. The caller's own copy is
      // assigned below, once the flight has handed one out.
      outcome.response = observedResponse;

      // Nobody is waiting on this any more -- MEASURED: the caller settles at
      // 18ms where the transport answers at 57ms -- so there is nothing to
      // REFUSE here, only an orphan holding a connection open. On the decoded
      // path that orphan keeps being read (52 pulls against 1 under `raw`),
      // because the decode never settles on a body that never closes and
      // `discardWhenLost` hangs on that same attempt (TO40).
      //
      // CLEANUP, NEVER A THROW. This region is shared: only the opener runs it
      // and every joiner waits on the same promise, so a throw here rejects all
      // of them. MEASURED: that form gave two joiners `abort` each.
      //
      // UNGUARDED, and measured to be so: adding a throw INSIDE this guard
      // survives all 1180 tests. Behind `flight === undefined` there is no
      // joiner left to reject and the caller is already gone, so the throw is
      // unobservable from here -- which is exactly why no test can catch it.
      // Forcing one would need a watcher the production path does not have.
      // The rule holds because the guard holds; remove the guard and a throw
      // becomes visible again (the joiner row in TO40 turns red). The form that
      // makes this structural rather than stated is the body-owner rework in
      // backstage-developer-portal-kkoh3.
      //
      // ONLY WITHOUT A FLIGHT. In a pool this body belongs to the joiners who
      // are decoding it right now, and cancelling it under them does not free a
      // connection nobody wants -- it breaks an answer somebody asked for.
      // MEASURED: without this guard both joiners fail to parse. The pooled
      // orphan is left standing on purpose; closing it needs the body-owner
      // rework, not a guard here.
      //
      // The condition is THIS CALL'S deadline, not the signal the exchange was
      // handed: that one is the flight's in a pool and the attempt clock under
      // `perAttemptTimeoutMs`, and neither says this call is over.
      if (flight === undefined && deadline.endedBy() !== undefined) {
        discardStream(rawResultOf(method, response));
      }

      const result =
        decodedOptions === undefined
          ? rawResultOf(method, response)
          : await decodedResultOf(method, url, decodedOptions, response, () =>
              madeHere(attributable),
            );
      // The very object the transport call above was handed, so there is no
      // second source to drift from.
      return { observed: observedResponse, result, sent: headers };
    };

    // One flight per pool, and this caller's own copy of what it answers with.
    // Without the option there is no table lookup at all (CO1).
    //
    // A FAILED flight rejects every participant with one object, so the catch
    // here is what gives this caller a failure of its own: its id, its
    // duration, its options and its own stack (CO59). It runs on a flight of
    // one as well, so a pool that happened to have a single caller cannot take
    // a different path (CO62).
    // What a JOINER reports about the wire comes from `shared.sent` below, and
    // that is only assigned once the flight has answered. A joiner on a flight
    // that FAILED is thrown past that line, so the catch here is where it learns
    // the one wire fact a failure still leaves standing. The opener reports its
    // own id on either outcome, because it set the headers itself.
    const boarded = (pool: Flight): Promise<Exchange> => {
      const joined = joinFlight(pool, deadline.signal, exchange, requestId);
      // The id this caller would have sent goes INTO the flight, and the id the
      // flight settled on comes back out. Written back so that if this caller
      // boards again on a following attempt it offers the pooled id rather than
      // its own; `joinFlight` on the line above is the only thing that reads it.
      requestId = joined.requestId;
      return joined.exchange.catch((error: unknown) => {
        // A shared request that reached the wire is ONE request, so the id it
        // carried names it for everybody it failed -- not only for whoever
        // happened to open the flight. Without this a thousand callers on one
        // failed flight leave a single log line traceable and the rest not.
        //
        // THE ID ALONE, and only for a caller holding no wire of its own. The
        // rest of what went out is the opener's trace and stays there, and an
        // opener (or an earlier attempt of this call) that did send keeps the
        // headers it really handed over. Asking rather than taking the pooled
        // id outright is what keeps the report honest if a flight can ever fail
        // before sending: nothing went out then, so there is nothing to name.
        //
        // Ahead of the rebuild, because `madeHere` reads these headers back.
        const adopted = adoptedFromFlight(joined.sentRequestId(), sentHeaders);
        sentHeaders = adopted ?? sentHeaders;
        requestsSent += adopted === undefined ? 0 : 1;
        throw forThisCaller(error, method, url, madeHere(final));
      });
    };

    // A KEPT ANSWER FIRST, and synchronously, so the lookup puts no await
    // between here and `joinFlight` and a miss still boards on the same tick
    // (CO68). A hit is not a request: only the per-caller work below runs on it.
    const cached = flight === undefined ? undefined : cachedExchangeOf(flight);
    const shared = await (flight === undefined
      ? // THE ATTEMPT'S OWN SIGNAL WHERE THERE IS ONE, which is the second
        // chooser this parameter was written for: alone, a call runs on its
        // merged deadline as it always did, and a per-attempt bound narrows
        // that to one attempt without the flight path ever seeing it.
        //
        // NOT ON THE POOLED PATH. A flight's request belongs to everybody who
        // joined it, so one participant's attempt clock may not abort work the
        // others are still waiting for -- the same reason the flight hands over
        // its own signal rather than the opener's (CO43, CO44).
        exchange(transportSignal())
      : keptOr(cached, () => boarded(flight))
    ).catch((error: unknown) => {
      // THIS caller's own abort state, on BOTH paths and after the rebuild.
      //
      // A caller that stopped waiting was not failed by the upstream, so the
      // breaker may not count it (BR23, BR8). MEASURED: without this line
      // three breaker rows go red, and they run on calls with no flight at
      // all -- which is why it sits out here rather than in the branch above.
      //
      // Out here and not inside `exchange`, because that region is shared and
      // only the OPENER runs it: asking there would give a joiner the
      // opener's abort state, and the rebuild would stamp it with the
      // joiner's own id (CO64).
      //
      // The attempt clock is asked last of the three, and only where the first
      // two say nothing (`endedFailure`). Where it does answer, the failure is
      // `AttemptTimeoutError` and not the `NetworkError` an abort would
      // otherwise be dressed as -- the loop repeats one of those and not the
      // other (ER73).
      throw endedFailure(final) ?? error;
    });

    // POST-BOARDING WORK ONLY, and the boundary sits exactly here on purpose.
    // Everything above stays in this file because it is scheduling-critical:
    // an enclosing async entry point before `joinFlight` lets a joiner miss the
    // flight and open a second transport (ER66, CO68, CO59, CO60).
    //
    // The call keeps what the call owns. `finishRequestAttempt` reports; these
    // lines decide. THE COUNT IS READ BEFORE `sentHeaders` IS OVERWRITTEN
    // (CO68, TR28): a caller holding no wire of its own rode the flight's one,
    // and an opener, or an attempt of this call that sent, counted its own
    // already and adds nothing.
    const finished = await finishRequestAttempt({
      decodedOptions,
      finalOptions: final,
      makeErrorOptions: madeHere,
      method,
      onExchangeObserved: snapshot => {
        outcome.response = snapshot.response;
      },
      // After the await rather than beside the `requestId` adoption above, and
      // that placement is the guarantee: an exchange exists only once the
      // transport has answered, so a caller reaching this line has a request to
      // name. One whose flight stranded earlier is thrown past it and keeps the
      // empty wire fields that say so. MEASURED: an opener whose body could not
      // be serialized shares nothing, and the caller behind it opens its own.
      onTransportSent: snapshot => {
        requestsSent += ridesFlight(sentHeaders);
        sentHeaders = snapshot.sentHeaders;
      },
      origin: originOf(cached, sentHeaders),
      requestId,
      shared,
      url,
    });

    outcome.body = finished.body;

    // Internally this ALWAYS throws -- `throwOnError` is a delivery choice on
    // the outermost layer, and suppressing the throw here would silently
    // disable retrying on status, because the loop retries what throws (TE4).
    //
    // Every observation field is copied into the call's own locals BEFORE the
    // throw, so the failure enters the unchanged attempt-local `catch` with
    // nothing left behind. `rawFailure` travels in the same value as the
    // failure it belongs to (ER46, ER47), which is what keeps a raw stream from
    // being orphaned by a transfer that took only half.
    if (finished.kind === 'status-failure') {
      rawFailure = finished.rawFailure;
      throw finished.failure;
    }

    return finished.result;
  };

  /**
   * Lets go of the raw result an abandoned attempt was holding.
   *
   * Only ONE of them can be held at a time, so without this the field is simply
   * overwritten by the next attempt and the stream behind it stays open until it
   * is collected -- one abandoned connection per failed attempt. Under `raw` the
   * library is the only one who can still reach it, because the caller never
   * received it: `deliverFailure` does the same for the attempt that ENDS the
   * call (ER46), and this is every attempt before that one.
   *
   * Cleared before the cancel is awaited, so nothing can hand out a result whose
   * stream is already on its way to being closed.
   */
  const discardRawFailure = async (): Promise<void> => {
    const stream = streamOf(rawFailure);
    rawFailure = undefined;
    await stream?.cancel().catch(() => {});
  };

  /**
   * The pause between two attempts, and the two ways it ends the call instead.
   *
   * THE DEADLINE STANDS ABOVE THE WAIT, but only in front of it. A wait that no
   * longer fits in what is left is not shortened but SKIPPED, so the real
   * failure goes out now rather than after a sleep that ends in a timeout
   * anyway. That is the whole of the deadline's say: a wait it lets through ends
   * at or before it, so it cannot come back and cut one short (RC37).
   *
   * The second throw is for the signal that CAN land mid-wait -- the caller's
   * own (RC17), and the deadline only where the wait spent exactly the last of
   * it. `sleep` returns early on either, and going straight on would send the
   * next attempt out on a call that is already over.
   *
   * The raw result is only let go of once the wait is committed to. Skipping
   * ends the call on THIS attempt, and under `raw` that result is still the
   * caller's to receive (ER46).
   */
  const waitForNextAttempt = async (failed: FailedAttempt): Promise<void> => {
    const retryDelayMs = retryDelayMsOf(failed);
    if (!worthWaiting(retryDelayMs, deadline.remaining())) throw failed.failure;
    notifyRetryScheduled(failed, retryDelayMs, url, deadline.endedBy());
    logRetry(failed, retryDelayMs);

    await discardRawFailure();
    await sleep(retryDelayMs, deadline.signal);

    // Asked here as well as in the outer race, which is what makes the two
    // agree: whichever settles first, the caller gets the same failure.
    const endedAfterWait = signalFailure(observedOptions);
    if (endedAfterWait !== undefined) throw endedAfterWait;
  };

  /* -------------------------------------------------------------------------
   * The observation window: one `after` per ATTEMPT, and none past the call.
   * The machine itself is {@link span}; what it means HERE is below.
   *
   * Two things close a window and neither may be dropped. Ordinarily the attempt
   * closes its own, when it settles. But the deadline can fire while an attempt
   * is still stuck, and that attempt never comes back to close anything -- so
   * the call closes it instead (`attemptSpan.seal()` in the `finally` of the
   * deadline race), because a handler counting upstream traffic has to hear
   * about the call that timed out most of all.
   *
   * Sealing is the other half of the same problem. The loop that lost the race
   * keeps running afterwards, and an attempt reporting to a caller who was
   * answered seconds ago is a worse report than the one this fixes: an
   * over-report nobody can place against a call that is finished.
   * ---------------------------------------------------------------------- */
  const attemptSpan = span({
    // `traceNow()` rather than the expression it wraps: ER72 says one builder,
    // so a failure and a result cannot describe the same call differently.
    onClose: () => runAfter(observedOptions, outcome, isolated, traceNow()),
    onOpen: () => {
      // Cleared rather than carried over. An attempt that fails before there is
      // a response would otherwise be reported with the PREVIOUS attempt's
      // response (AF11 read backwards), and one that finally succeeds would
      // carry the error of the attempt before it.
      //
      // Deleted rather than set to `undefined`, so a second attempt is handed
      // the same shape a first one is: AF11 reads absence, and a key that is
      // always there with nothing in it is the shape DECISION 3.6 rules out
      // everywhere else in this file.
      sentHeaders = undefined;
      outcome.body = undefined;
      delete outcome.error;
      delete outcome.response;
    },
  });

  /**
   * The attempts, and nothing else.
   *
   * Around `run` and inside the deadline, which is the position the whole design
   * turns on. Everything `run` does — the `before` handlers, the serializer, the
   * decode, the cap — therefore happens per attempt, while ONE clock covers all
   * of them together. And because `throwOnError` is read outside `settled`, it
   * judges what the loop finally settled on rather than each attempt on its way
   * past.
   *
   * The `after` handlers are inside it too, one per attempt, through the
   * observation window above. MEASURED against three failing attempts: three
   * `before` calls, three serializations, three decodes, three `after` calls.
   *
   * The failure that ends the loop is rethrown untouched: not the first one kept
   * aside, and not a summary. A caller branching on `instanceof NotFoundError`
   * gets the answer the upstream actually gave last (RC4).
   */
  const attempts = async (): Promise<UntracedResult<unknown>> => {
    const retryConfig = retryConfigOf(retryAtEntry);
    for (let attempt = 1; ; attempt += 1) {
      attemptsStarted += 1;
      attemptSpan.open();
      // Before the attempt rather than after it, because this one counts what
      // goes out and not what came back. The loop cannot reach here on a call
      // that is already over: a wait past the deadline is skipped rather than
      // served, so there is no iteration to over-report on.
      if (retryConfig !== undefined)
        notifyAttemptStarted(retryConfig, attempt, url);
      // HERE, so the budget is worked out after the preceding wait has been
      // spent and before anything of this attempt runs (RC51, RC74).
      attemptClock = armAttemptClock(retryConfig);
      try {
        try {
          const result = await run();
          attemptSpan.close();
          return result;
        } finally {
          // The moment the attempt settles, either way, and BEFORE the wait
          // that may follow it: a timer left armed would abort the NEXT
          // attempt's signal, and a merge left unreleased stacks one listener
          // per attempt on a deadline signal that lives as long as the call
          // (RC70). Cleared as well as stopped, so a finished clock cannot
          // answer `expired()` for an attempt it did not run.
          attemptClock?.stop();
          attemptClock = undefined;
        }
      } catch (error) {
        // Written down per attempt, because an observer is handed what THIS
        // attempt ended on rather than what the loop finally gave up with. Only
        // what the library itself labelled: a `before` handler's own throw stays
        // the caller's (BE7), and dressing it up as one of ours would be a claim
        // about a failure this library did not produce.
        //
        // Via `oursToLabel`, because this is BOOKKEEPING on the way to
        // rethrowing and bookkeeping may not take the failure away from its
        // caller. See that helper for what a bare read would cost here.
        if (oursToLabel(error)) outcome.error = error;
        attemptSpan.close();
        if (retryConfig === undefined) throw error;
        const failed = { attempt, config: retryConfig, failure: error };
        if (!mayRetry(failed)) throw error;
        await waitForNextAttempt(failed);
      }
    }
  };

  /**
   * The retry loop with the `policies` list around it.
   *
   * OUTSIDE THE LOOP and INSIDE THE CLOCK, which is the whole of what the list
   * is for. Outside the loop, a policy observes one outcome per CALL rather than
   * one per attempt (PO8): a breaker placed inside would open three times as
   * fast as intended on a single flaky call, and one that calls the call twice
   * buys two full rounds of attempts rather than two attempts (PO7). Inside the
   * clock, a policy that waits spends the deadline, so a call can expire while a
   * throttle still has it queued.
   *
   * Outside the `before` handlers too, because they run inside `run`, which the
   * loop calls (PO19). A throttle has therefore already waited and a bulkhead
   * has already handed out its slot before the first handler runs — which is the
   * price stated on `Policy`, not an accident of where this line sits.
   *
   * A `retry` given as a `Policy` is composed by the same call, innermost of the
   * lot (RY3, RY7). It takes this position over rather than joining `policies`,
   * so what it wraps is the attempts and what wraps IT is the list — see
   * `guarded` for why that layer matters.
   */
  const guardedAttempts = guarded(options, deadline, attempts, callId, by =>
    failureFor(by, observedOptions),
  );

  /**
   * Everything the delivery decision sits outside of.
   *
   * A promise of its own rather than the body of `request`, so that the retry
   * loop and `coalesce` land INSIDE it when they arrive and the decision below
   * does not have to move. That position is the whole design: an option that
   * suppressed the throw further in would silently disable retrying on status,
   * because the loop retries what throws.
   */
  const settled = async (): Promise<UntracedResult<unknown>> => {
    try {
      // BEFORE THE FIRST ATTEMPT, and that position is the whole of UR21. Read
      // from inside the loop, a number the library cannot act on became a
      // failure the loop could repeat: with `timeout: 0` leaving the clock
      // unarmed and `maxAttempts: Infinity` leaving the budget open, the call
      // spun on its own refusal and never settled (TO36). Read here it is
      // answered once, before any policy has admitted the call and before any
      // handler of the caller's has run.
      //
      // Inside the `try`, so the `finally` below still stops the clock: an
      // unusable `retry` beside a perfectly good `timeout` would otherwise leave
      // an armed timer behind for the rest of that deadline.
      rethrowAs(
        () => validateOptions(options),
        cause =>
          new UnsupportedRequestError(method, url, {
            ...madeHere(options),
            cause,
          }),
      );
      // The race lets go of the losing attempt, and `discardWhenLost` is what
      // picks it back up: see there for why the deadline owns the flag.
      const attempt = guardedAttempts();
      let deadlineWon = false;
      discardWhenLost(
        attempt,
        () => deadlineWon,
        () => {
          // Read and cleared at settle time, so nothing can hand out a result
          // whose stream is already on its way to being closed, exactly as
          // `discardRawFailure` does between two attempts.
          const held = rawFailure;
          rawFailure = undefined;
          return held;
        },
      );
      return await Promise.race([
        attempt,
        deadline.ended.then(by => {
          deadlineWon = true;
          throw failureFor(by, observedOptions);
        }),
      ]);
    } catch (error) {
      // The DEADLINE's half of what the loop already does for itself. A failure
      // the loop produced has been written down by the attempt that produced it;
      // one the race produced belongs to no attempt, and this is the only place
      // it gets written down before the `finally` below reports it.
      //
      // Only what the library itself classified, either way. A `before`
      // handler's own throw reaches the caller untouched (BE7), and dressing it
      // up as one of ours here would be a claim about a failure this library did
      // not produce.
      outcome.endedWith = error;
      // Via `oursToLabel`, one statement before we hand the caller their own
      // failure back. A bare read here would hand back their getter's instead.
      if (oursToLabel(error)) outcome.error = error;
      throw error;
    } finally {
      // Every exit passes here, the raw handover included: that one RESOLVES with
      // the clock still running (TO6), so without this a finished call leaves a
      // timer behind for the rest of its deadline -- two minutes on the default.
      deadline.stop();
      // AF1: always. The `finally` is the whole promise — a failed call and a
      // `before` that threw both land here, and both without a response. On the
      // ordinary path the attempt has already reported and this closes nothing;
      // it is the attempt the deadline outran that needs it.
      attemptSpan.seal();
      // Here rather than around the two `return`s below, for the same reason
      // the seal above is: this is the one place EVERY exit passes, the
      // rethrow under `throwOnError` included. `outcome` holds what the last
      // attempt ended on, so the line reports the answer the caller gets.
      callLog(method, url, outcome.response?.status, outcome.endedWith);
    }
  };

  /**
   * The one place the answer becomes the caller's, so the one place it is told
   * what this call did (`TR25`). Both exits below pass through here.
   */
  try {
    return { ...(await settled()), trace: traceNow() };
  } catch (error) {
    // On the options the handlers left behind, like every other option (TE9):
    // `observedOptions` is `final` from the moment `runBefore` has returned.
    return await deliverFailure(error, observedOptions, rawFailure, traceNow());
  }
}

const globalHeaders: GlobalRequestHeaders = {};

/**
 * Headers sent on every call that does not set `isolate: true`, merged with the
 * per-call `headers`, which win
 * on a collision. A `null` per call removes a global header for that call.
 *
 * @remarks
 * Read on every call rather than captured, so a caller who REPLACES the
 * object gets the same result as one who mutates it.
 *
 * @privateRemarks
 * Assigned onto the function rather than composed with `Object.assign`, which
 * cost a second name for the same thing plus an explicit annotation to go with
 * it. Neither turned out to be necessary: TypeScript widens a function
 * declaration's type to include properties assigned in the same scope, overloads
 * included.
 *
 * MEASURED: the function reading `request.headers` from inside its own body
 * is not the circularity the old note claimed it was.
 */
request.headers = globalHeaders;

/**
 * The transport every call goes out on unless it brings its own. Unset at
 * startup: assign it once where the app starts.
 *
 * @privateRemarks
 * `noTransport()` rather than a bare `undefined`: that would be narrowed to the
 * literal type by control flow, and the property would then refuse the
 * transport an app assigns to it later. FE18.
 */
request.fetch = noTransport();

/**
 * What every call asks for unless it says otherwise. Unset at startup.
 *
 * @privateRemarks
 * `noTrace()` for the reason given on `request.fetch`. TR13.
 */
request.trace = noTrace();

/**
 * Registers a handler that runs before EVERY call in the process, after that
 * call's own list. Assigned onto the function for the same reason as
 * `request.headers` above.
 */
request.before = (handler: GlobalBeforeRequestHandler): Unregister =>
  registerGlobalBefore(handler);

/** Registers an observer that runs after EVERY call in the process. */
request.after = (handler: GlobalAfterRequestHandler): Unregister =>
  registerGlobalAfter(handler);
